/** IXD 편집기 단위 변환 유틸리티 */

import type { LineItemUnit } from "@/apis/compare/types";
import { parseCellId } from "@/utils/ixd-cell-model";
import { formatNumericStoredForDisplay } from "@/utils/value-conversion";

/**
 * unitText → divisor 변환.
 * "천원" → 1000, "백만원" → 1_000_000, "원"/null → 1
 */
export function getUnitDivisor(unitText: string | null | undefined): number {
  if (!unitText) return 1;
  if (unitText.includes("백만")) return 1_000_000;
  if (unitText.includes("천")) return 1_000;
  return 1;
}

/**
 * divisor → 공유 변환 모듈의 scale(10^k). 10^k(k≥0)이 아니면 null.
 * 생산자는 `getUnitDivisor`/`getUnitDivisorFromExponent`(1·10³·10⁶)뿐이라 정상 경로에서는
 * 항상 유효하다 — null은 DOM dataset이 깨졌다는 뜻이므로 호출부가 폴백·거절한다.
 */
export const toUnitScale = (divisor: number): number | null => {
  if (!Number.isFinite(divisor) || divisor < 1) return null;
  const k = Math.round(Math.log10(divisor));
  return 10 ** k === divisor ? k : null;
};

/** 최종산출물 API의 단위 지수(0/3/6) → 실제 제수. */
export function getUnitDivisorFromExponent(unit: number): number {
  if (unit === 3) return 1_000;
  if (unit === 6) return 1_000_000;
  return 1;
}

/** 실제 제수 → 사용자 표시 단위. */
export function getUnitLabel(divisor: number): string {
  if (divisor === 1_000) return "천원";
  if (divisor === 1_000_000) return "백만원";
  if (divisor === 1) return "원";
  return `${divisor.toLocaleString()}배`;
}

/** 최종산출물 API의 단위 지수(0/3/6) → 사용자 표시 단위. */
export function getUnitLabelFromExponent(unit: number): string {
  return getUnitLabel(getUnitDivisorFromExponent(unit));
}

/** 주 배율 지수 → 표시 라벨. 원화 라벨의 "원" 자리에 "주"가 온다. 예: 3 → "천주". */
export function getSharesUnitLabelFromExponent(unit: number): string {
  return `${getUnitLabelFromExponent(unit).replace(/원$/, "")}주`;
}

/**
 * 외화 라인아이템 단위 → 사용자 표시 라벨. 예: {USD, 3} → "USD 천", {USD, 0} → "USD".
 * 원화 라벨(getUnitLabel)의 "원" 자리에 통화 코드가 오므로 배수 접두만 붙인다.
 *
 * 0/3/6 밖의 지수는 배수를 그대로 적는다(예: {USD, 2} → "USD ×100"). 셀 환산은
 * `10 ** unit` 이라 지수마다 나누는 값이 다른데, 라벨을 "USD"로 접으면 서로 다른
 * 배율이 같은 글자로 보여 배율 혼재·불일치 경고가 차이를 보여주지 못한다.
 */
export function getForeignCurrencyUnitLabel({
  currency,
  unit,
}: LineItemUnit): string {
  if (unit === 0) return currency;
  if (unit === 3) return `${currency} 천`;
  if (unit === 6) return `${currency} 백만`;
  return `${currency} ×${(10 ** unit).toLocaleString("en-US")}`;
}

/**
 * 외화 단위 목록에서 표시용 라벨을 중복 없이 뽑는다. IXD 는 라인아이템 단위 맵
 * (lineItemId → 단위), DSD 워크북은 표 단위 선언 배열을 준다.
 * 같은 통화·배수가 여럿이어도 라벨은 한 번만 나온다. 순서는 등장 순.
 */
export function getForeignCurrencyUnitLabels(
  units: Record<string, LineItemUnit> | LineItemUnit[] | null | undefined,
): string[] {
  if (!units) return [];
  const list = Array.isArray(units) ? units : Object.values(units);
  return [...new Set(list.map(getForeignCurrencyUnitLabel))];
}

export interface MixedScaleForeignCurrency {
  currency: string;
  /** 배율별 묶음. 지수 오름차순. */
  scales: {
    unit: number;
    label: string;
    /** 이 배율을 가진 라인아이템. 배열 입력(DSD 워크북 표 단위 선언)은 라인아이템을 모르므로 비어 있다. */
    lineItemIds: string[];
  }[];
}

/**
 * 한 표 안에서 같은 통화가 서로 다른 배율로 선언된 통화를 등장 순으로 뽑고,
 * 배율마다 어떤 라인아이템이 그 배율인지 묶는다.
 * 예: {a:{USD,0}, b:{USD,3}, c:{EUR,3}} → [{USD, [{0,"USD",[a]}, {3,"USD 천",[b]}]}].
 *
 * 지수로 비교한다 — 셀 환산이 `10 ** unit` 이라 지수가 곧 나누는 값이다.
 */
export function getMixedScaleForeignCurrencies(
  units: Record<string, LineItemUnit> | LineItemUnit[] | null | undefined,
): MixedScaleForeignCurrency[] {
  if (!units) return [];
  const entries: [string | null, LineItemUnit][] = Array.isArray(units)
    ? units.map((unit) => [null, unit])
    : Object.entries(units);

  const lineItemIdsByUnitByCurrency = new Map<string, Map<number, string[]>>();
  for (const [lineItemId, { currency, unit }] of entries) {
    const byUnit =
      lineItemIdsByUnitByCurrency.get(currency) ?? new Map<number, string[]>();
    const lineItemIds = byUnit.get(unit) ?? [];
    if (lineItemId !== null) lineItemIds.push(lineItemId);
    byUnit.set(unit, lineItemIds);
    lineItemIdsByUnitByCurrency.set(currency, byUnit);
  }

  return [...lineItemIdsByUnitByCurrency]
    .filter(([, byUnit]) => byUnit.size > 1)
    .map(([currency, byUnit]) => ({
      currency,
      scales: [...byUnit]
        .sort(([a], [b]) => a - b)
        .map(([unit, lineItemIds]) => ({
          unit,
          label: getForeignCurrencyUnitLabel({ currency, unit }),
          lineItemIds,
        })),
    }));
}

/**
 * 여러 범위(예: 기간 섹션)에서 따로 판정한 배율 혼재를 하나로 합친다. 통화·배율별로
 * 라인아이템을 합집합으로 모으고, 통화는 처음 나온 순서를 따른다.
 * 각 범위에서 섞인 통화만 들어오므로 범위 사이에만 갈리는 배율은 새로 만들지 않는다.
 */
export function mergeMixedScaleForeignCurrencies(
  lists: readonly MixedScaleForeignCurrency[][],
): MixedScaleForeignCurrency[] {
  const byCurrency = new Map<
    string,
    Map<number, { label: string; ids: Set<string> }>
  >();
  for (const { currency, scales } of lists.flat()) {
    const byUnit = byCurrency.get(currency) ?? new Map();
    for (const { unit, label, lineItemIds } of scales) {
      const entry = byUnit.get(unit) ?? { label, ids: new Set<string>() };
      lineItemIds.forEach((id) => entry.ids.add(id));
      byUnit.set(unit, entry);
    }
    byCurrency.set(currency, byUnit);
  }
  return [...byCurrency].map(([currency, byUnit]) => ({
    currency,
    scales: [...byUnit]
      .sort(([a], [b]) => a - b)
      .map(([unit, { label, ids }]) => ({
        unit,
        label,
        lineItemIds: [...ids],
      })),
  }));
}

/** 배율 혼재 경고 문구. 캡션과 값 반영 토스트가 같은 문장을 쓰도록 여기 한 곳에 둔다. */
export const getMixedScaleWarningText = (
  currencies: readonly MixedScaleForeignCurrency[],
): string =>
  `${currencies.map(({ currency }) => currency).join(", ")} 배율이 행마다 다릅니다. 행별 단위를 확인하세요.`;

export interface MixedScaleLineItemRow {
  key: string;
  /** 배율 라벨. 예: "USD 천" */
  label: string;
  /** 그 배율을 가진 태그 라벨을 ", " 로 이은 것 */
  lineItems: string;
}

/**
 * 배율별로 어떤 태그가 그 배율인지 표시용 줄로 편다. 예: [{label:"USD 천", lineItems:"차입금, 사채"}].
 * 라벨을 못 찾은 라인아이템은 ID 를 그대로 쓴다. 라인아이템을 모르는 배율(배열 입력)은 뺀다.
 */
export const getMixedScaleLineItemRows = (
  currencies: readonly MixedScaleForeignCurrency[],
  lineItemLabels?: ReadonlyMap<string, string>,
): MixedScaleLineItemRow[] =>
  currencies.flatMap(({ currency, scales }) =>
    scales
      .filter(({ lineItemIds }) => lineItemIds.length > 0)
      .map(({ unit, label, lineItemIds }) => ({
        key: `${currency}:${unit}`,
        label,
        lineItems: lineItemIds
          .map((id) => lineItemLabels?.get(id) ?? id)
          .join(", "),
      })),
  );

/**
 * 셀 하나의 외화 라인아이템 단위. 원화 셀이면 undefined.
 * 셀 값 환산(table-body-cell.tsx)과 외화 캡션이 같은 해석을 쓰도록 여기 한 곳에 둔다.
 */
export function getLineItemUnitForCell(
  lineItemUnits: Record<string, LineItemUnit> | null | undefined,
  cellId: string,
): { lineItemId: string; unit: LineItemUnit } | undefined {
  if (!lineItemUnits) return undefined;
  const { lineItemId } = parseCellId(cellId);
  const unit = lineItemUnits[lineItemId];
  return unit ? { lineItemId, unit } : undefined;
}

/**
 * 라인아이템 단위 맵을 표에 실제로 그려지는 셀의 라인아이템으로 좁힌다.
 *
 * 서버의 `lineItemUnits` 는 DL(주석 목차) 전체의 외화 라인아이템을 담는다. 같은 DL 에
 * 묶였을 뿐 이 표에 없는 태그의 단위가 캡션에 나오면 사용자는 표에 외화 행이 있다고 읽는다.
 */
export function pickLineItemUnitsInRows(
  lineItemUnits: Record<string, LineItemUnit> | null | undefined,
  rows: ReadonlyArray<{ columns: ReadonlyArray<{ id?: string | null }> }>,
): Record<string, LineItemUnit> {
  const picked: Record<string, LineItemUnit> = {};
  for (const row of rows) {
    for (const column of row.columns) {
      if (!column.id) continue;
      const found = getLineItemUnitForCell(lineItemUnits, column.id);
      if (found) picked[found.lineItemId] = found.unit;
    }
  }
  return picked;
}

/**
 * 원 값 → 단위 적용 값 (반올림).
 * 예: applyUnit(123456789, 1000) → 123457
 */
export function applyUnit(rawValue: number, divisor: number): number {
  if (divisor === 1) return rawValue;
  return Math.round(rawValue / divisor);
}

/**
 * 숫자 → 표기 문자열.
 * 양수: "123,457", 음수: "(123,457)", 0: "0"
 */
export function formatDisplayValue(value: number): string {
  if (value < 0) {
    return `(${Math.abs(value).toLocaleString()})`;
  }
  return value.toLocaleString();
}

/**
 * 문자열 → 숫자 파싱.
 * 콤마·괄호·공백 허용: "(123,457)" → -123457, "123,457" → 123457
 * 숫자가 아닌 문자열이면 null 반환.
 */
export function parseNumericValue(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === "") return null;

  // 하이픈 단독 → 0 (재무제표에서 "값 없음" 표기)
  if (trimmed === "-" || trimmed === "–" || trimmed === "—") return 0;

  const isParenNegative = trimmed.startsWith("(") && trimmed.endsWith(")");
  const stripped = isParenNegative
    ? trimmed.slice(1, -1).replace(/,/g, "")
    : trimmed.replace(/,/g, "");

  const num = Number(stripped);
  if (isNaN(num)) return null;
  return isParenNegative ? -num : num;
}

/**
 * monetary 표시 전용: 문자열 십진 연산으로 단위(10^k) 적용 + 자릿수 반올림(half-up).
 * 연산은 공유 모듈(value-spec 06)이 소유하고, 여기서는 divisor→scale 조립과
 * "폴백해야 하는가"(null) 판정만 한다.
 *
 * 자릿수 규칙 (백엔드 apply_unit_to_text 와 1:1 동일):
 * - decimalDigits(.frm CurrencyDecimalDigits) 지정 시 그 자릿수로 고정 표기.
 *   예: ("123456", 1000, 2) → "123.46", ("1234.5", 1, 2) → "1,234.50"
 * - 미지정 + 원본이 정수면 정수로 반올림 (기존 표기 유지). 예: ("123456789", 1000) → "123,457"
 * - 미지정 + 원본이 소수면 환산 결과의 소수를 절삭 없이 그대로 표기.
 *
 * negated 는 표시 부호 반전(값 부호와 XOR). 음수는 "(1,234.50)" 괄호 표기.
 * divisor 가 10^k 꼴이 아니거나 숫자가 아닌 텍스트면 null 반환 (호출부 fallback).
 */
export function formatMonetaryTextForDisplay({
  rawText,
  divisor,
  decimalDigits = null,
  negated = false,
}: {
  rawText: string;
  divisor: number;
  decimalDigits?: number | null;
  negated?: boolean;
}): string | null {
  // divisor<1(음수 지수)은 소수점 시프트가 no-op이 되어 원본이 단위 미적용으로
  // 노출되므로 명시적으로 거부한다 (호출부 fallback)
  const scale = toUnitScale(divisor);
  if (scale === null) return null;

  return formatNumericStoredForDisplay(rawText, {
    kind: "MONETARY",
    scale,
    decimalDigits,
    negated,
  });
}

/**
 * 편집 진입 초기값용 단위 적용 문자열.
 *
 * 표시(formatMonetaryTextForDisplay)와 같은 문자열 십진 경로라 표시 자릿수
 * (decimalDigits)와 1:1로 일치하고, Number 경유(applyUnit)의 대형 정수 정밀도
 * 손실이 없다. 편집값 규격에 맞춰 음수는 minus 부호 · negated 반전 없음(편집값은
 * 저장 부호 그대로). 파싱 불가면 null (호출부 fallback).
 *
 * 콤마는 **남긴다**(WXBRL-2687) — value-spec 06 §3의 "콤마 제거"는 이 변경으로
 * 낡았고 submodule이라 후속 PR에서 고친다. §3을 근거로 콤마 제거를 되살리면
 * 편집창 천단위 표시가 조용히 사라지므로, 스펙을 먼저 고치고 이 주석을 지울 것.
 */
export function formatMonetaryTextForEditing({
  rawText,
  divisor,
  decimalDigits = null,
}: {
  rawText: string;
  divisor: number;
  decimalDigits?: number | null;
}): string | null {
  const display = formatMonetaryTextForDisplay({
    rawText,
    divisor,
    decimalDigits,
  });
  if (display === null) return null;
  // 콤마는 남긴다 — 편집 입력창도 천단위 구분을 보여준다. 괄호→minus는
  // displayTextToEditingText 한 곳이 소유하므로 거기에 맡기고(콤마까지 벗겨진다),
  // 타이핑 중 재삽입과 같은 insertThousandSeparators로 다시 붙인다 — 편집 초기값과
  // 타이핑 중 포맷이 같은 함수로 보장된다(strip→재삽입은 멱등).
  return insertThousandSeparators(displayTextToEditingText(display));
}

/**
 * 표시 문자열 → 편집 입력 문자열. 콤마 제거, 괄호 음수 → minus 부호.
 * 자릿수(decimalDigits 패딩)는 그대로 두므로 편집창 초기값이 표시 자릿수와 일치한다.
 * monetary·비monetary 숫자 kind가 같은 규칙을 쓰도록 여기 한 곳에 둔다.
 */
export const displayTextToEditingText = (display: string): string => {
  const bare = display.replace(/,/g, "");
  return bare.startsWith("(") && bare.endsWith(")")
    ? `-${bare.slice(1, -1)}`
    : bare;
};

/**
 * 비교 뷰 표시 전용: 원본 텍스트의 정수부에만 천단위 쉼표를 삽입한다.
 * Number() 변환 없이 문자열 처리하므로 소수 자릿수·대형 정수의 정밀도가 유지된다.
 * 하이픈(–, —, -)과 숫자가 아닌 텍스트는 원본 그대로 반환한다.
 */
export function insertThousandSeparators(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return text;

  // 하이픈(값 없음) → 원본 그대로
  if (trimmed === "-" || trimmed === "–" || trimmed === "—") return text;

  const isParenNegative = trimmed.startsWith("(") && trimmed.endsWith(")");
  const inner = isParenNegative ? trimmed.slice(1, -1) : trimmed;

  const dotIdx = inner.indexOf(".");
  const intPart = dotIdx >= 0 ? inner.slice(0, dotIdx) : inner;
  const decPart = dotIdx >= 0 ? inner.slice(dotIdx) : "";

  // 정수부가 순수 숫자(음수 부호 포함)가 아니면 원본 그대로
  if (!/^-?\d+$/.test(intPart)) return text;

  const absIntStr = intPart.startsWith("-") ? intPart.slice(1) : intPart;
  const sign = intPart.startsWith("-") ? "-" : "";
  const withCommas = absIntStr.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  const result = sign + withCommas + decPart;
  return isParenNegative ? `(${result})` : result;
}
